/**
 * ScoutWatchlist — раздел «Вотчлист», дизайн «Aikido Invest» (tz-redesign-analyzer-watchlist §2).
 * Данные — живые из /market/strangle-analysis (рыночная часть кешируется per-ticker).
 * Покрытие считается КЛИЕНТСКИ от панели конс. части: годовой доход подушки ≥ стоимости стрэнгла (§2.2).
 * Обновление данных — 3×/день на бэке (cron), без ручного автообновления; статус-плашка сверху.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api";
import { LBL, tip } from "../labels";
import { Tip } from "./Tooltip";
import { strangleBreakevenMove } from "../lib/breakeven";

// --- conservative part (подушка) — capital/rate, persist ---
interface Cons { sum: number; rate: number; }
const CONS_KEY = "strangcalc.budget.v1";   // совместимо с прежним ключом (capital/rate)
function loadCons(): Cons {
  try { const r = localStorage.getItem(CONS_KEY); if (r) { const o = JSON.parse(r); return { sum: o.capital ?? o.sum ?? 5000, rate: o.rate ?? 6 }; } } catch { /* */ }
  return { sum: 5000, rate: 6 };
}
function saveCons(c: Cons) { try { localStorage.setItem(CONS_KEY, JSON.stringify({ capital: c.sum, rate: c.rate })); } catch { /* */ } }

// --- список тикеров (с классом + scout-метки) ---
// Источник правды — сервер (/market/tracked-symbols); localStorage — кеш мгновенной отрисовки.
interface Ticker { t: string; n: string; cls: string; prio?: string | null; trig?: string | null; sdate?: string | null; }
const PROXY: Record<string, string> = { "XAU/USD": "GLD", "EUR/USD": "FXE", "USD/JPY": "FXY" };
const DEFAULT_TICKERS: Ticker[] = [
  { t: "GLD", n: "Gold ETF", cls: "Металлы" }, { t: "SLV", n: "Silver ETF", cls: "Металлы" },
  { t: "CPER", n: "Copper ETF", cls: "Металлы" }, { t: "URA", n: "Uranium ETF", cls: "Сырьё" },
  { t: "USO", n: "Oil ETF", cls: "Сырьё" }, { t: "WEAT", n: "Wheat ETF", cls: "Сырьё" },
  { t: "MOS", n: "Mosaic · удобрения", cls: "Сырьё" }, { t: "TLT", n: "Treasury bonds", cls: "Облигации" },
  { t: "XLE", n: "Energy ETF", cls: "Индексы" }, { t: "SPY", n: "S&P 500", cls: "Индексы" },
  { t: "SMH", n: "Semiconductors", cls: "Индексы" }, { t: "FXI", n: "China large-cap", cls: "Индексы" },
  { t: "EWJ", n: "Japan ETF", cls: "Индексы" }, { t: "EWY", n: "South Korea ETF", cls: "Индексы" },
  { t: "TSM", n: "TSMC ADR", cls: "Индексы" }, { t: "ITA", n: "Defense ETF", cls: "Индексы" },
  { t: "IBIT", n: "Bitcoin ETF", cls: "Крипто" }, { t: "USD/JPY", n: "FX → FXY", cls: "FX" },
  { t: "EUR/USD", n: "FX → FXE", cls: "FX" }, { t: "XAU/USD", n: "Gold FX → GLD", cls: "FX" },
];
const TK_KEY = "strangcalc.scout_tickers.v1";
function loadTickers(): Ticker[] {
  try { const r = localStorage.getItem(TK_KEY); if (r) { const a = JSON.parse(r); if (Array.isArray(a) && a.length) return a; } } catch { /* */ }
  return DEFAULT_TICKERS;
}
function saveTickers(a: Ticker[]) { try { localStorage.setItem(TK_KEY, JSON.stringify(a)); } catch { /* */ } }

// --- кеш рыночной части анализа (per ticker) ---
interface Mkt {
  spot?: number; callK?: number; putK?: number; sigmaFull?: number; exitFull?: number;
  ivRank?: number | null; ivStatus?: string; expiry?: string; days?: number; source?: string;
  beDownPct?: number | null; beUpPct?: number | null;   // окупаемость премии (общий параметр)
  beLow?: number | null; beHigh?: number | null;        // цены точек нуля (для тултипа)
  hv2y?: number | null; hv10y?: number | null;          // историческая вола 2г/10л (доля)
  // реальный годовой размах (ход за год от входа, доли): best — макс направление, up/dn — последний год
  rBest?: number | null; rUp?: number | null; rDn?: number | null; rUpLast?: number | null; rDnLast?: number | null;
  fetchedAt?: number; error?: string; loading?: boolean;
}
const MKT_KEY = "strangcalc.scout_mkt.v4";   // v4: + annual_range; обновление по свежести (сессия)
function loadMkt(): Record<string, Mkt> {
  try { const r = localStorage.getItem(MKT_KEY); if (r) return JSON.parse(r); } catch { /* */ }
  return {};
}
function saveMkt(m: Record<string, Mkt>) { try { localStorage.setItem(MKT_KEY, JSON.stringify(m)); } catch { /* */ } }

const LIMIT = 10;
const f0 = (n: number | undefined) => (n == null || Number.isNaN(n)) ? "—" : "$" + Math.round(n).toLocaleString("ru");
// Страйк без лишнего «.0»: целое → «410», дробный → «22.5».
const fk = (n: number) => { const r = Math.round(n * 10) / 10; return Number.isInteger(r) ? String(r) : String(r); };
const expFmt = (iso?: string) => { if (!iso) return "—"; const [y, m, d] = iso.split("-"); return `${d}.${m}.${y.slice(2)}`; };

type SortKey = "iv" | "prem" | "p9" | "exp" | "hv";

export function ScoutWatchlist() {
  const nav = useNavigate();
  // Drill-down: тикер → Аналитик, где автоматически соберутся обе ноги и построится график
  const openAnalyzer = (t: Ticker) => {
    const dataSym = PROXY[t.t] ?? t.t;
    nav(`/portfolios/new?symbol=${encodeURIComponent(dataSym)}&from=${encodeURIComponent(t.t)}`);
  };
  const [tickers, setTickers] = useState<Ticker[]>(() => loadTickers());
  const [cons, setCons] = useState<Cons>(() => loadCons());
  const [mkt, setMkt] = useState<Record<string, Mkt>>(() => loadMkt());
  const [sortKey, setSortKey] = useState<SortKey>("iv");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [cls, setCls] = useState<string>("");
  const [ivMax, setIvMax] = useState<number>(100);
  const [hideUncovered, setHideUncovered] = useState(false);
  const [hideHvLow, setHideHvLow] = useState(false);

  // Клик по заголовку: тот же столбец — разворот направления; другой — выбрать его (asc).
  function clickSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  }
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : "");
  const [expanded, setExpanded] = useState(false);
  const [ivSnap, setIvSnap] = useState<api.IvHistoryLatest | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [delTk, setDelTk] = useState<string | null>(null);
  const [addInp, setAddInp] = useState("");
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const flipPrev = useRef<Record<string, number>>({});

  useEffect(() => { saveTickers(tickers); }, [tickers]);
  useEffect(() => { saveCons(cons); }, [cons]);
  useEffect(() => { saveMkt(mkt); }, [mkt]);
  useEffect(() => { api.fetchIvHistoryLatest().then(setIvSnap).catch(() => {}); }, []);
  // Серверный список — источник правды (scout-метки приезжают оттуда же).
  useEffect(() => {
    api.fetchTrackedSymbols(true).then((rows) => {
      if (!rows.length) return;
      setTickers(rows.map((s) => ({
        t: s.symbol, n: s.name || "Инструмент", cls: s.cls || "—",
        prio: s.priority, trig: s.trigger, sdate: s.signal_date,
      })));
    }).catch(() => { /* офлайн/старый бэк → остаёмся на localStorage-кеше */ });
  }, []);

  const income = (cons.sum || 0) * (cons.rate || 0) / 100;

  // --- фон-загрузка живых данных (последовательно, тихо) ---
  async function loadOne(t: Ticker) {
    const sym = PROXY[t.t] ?? t.t;
    setMkt((m) => ({ ...m, [t.t]: { ...m[t.t], loading: true } }));
    try {
      const a = await api.analyzeStrangle({ symbol: sym, lot_multiplier: 100, capital: cons.sum, rate: cons.rate });
      const be = strangleBreakevenMove(a.spot, a.call.strike, a.put.strike, a.call.premium_now, a.put.premium_now);
      setMkt((m) => ({ ...m, [t.t]: {
        spot: a.spot, callK: a.call.strike, putK: a.put.strike,
        sigmaFull: a.coverage.premium_full, exitFull: a.coverage.premium_at_exit_full,
        ivRank: a.iv_rank.iv_rank_pct, ivStatus: a.iv_rank.status,
        expiry: a.expiration, days: a.days_to_expiration, source: a.data_source,
        beDownPct: be.downPct, beUpPct: be.upPct, beLow: be.lowBE, beHigh: be.highBE,
        hv2y: a.hv_2y ?? null, hv10y: a.hv_10y ?? null,
        rBest: a.annual_range?.best_typ ?? null, rUp: a.annual_range?.up_typ ?? null, rDn: a.annual_range?.dn_typ ?? null,
        rUpLast: a.annual_range?.up_last ?? null, rDnLast: a.annual_range?.dn_last ?? null,
        fetchedAt: Date.now(), loading: false, error: undefined,
      } }));
    } catch (e: any) {
      setMkt((m) => ({ ...m, [t.t]: { ...m[t.t], loading: false, error: e.message ?? String(e) } }));
    }
  }
  const loadingRef = useRef(false);
  // Кеш считается СВЕЖИМ, если подтянут сегодня и не старше 3 часов. Иначе (открыли утром,
  // данные с прошлой сессии) — перезапрашиваем. Так «сессия открылась → свежие данные»
  // работает, но без «дёрганья» при многократном заходе за одну сессию.
  function isFresh(cur?: Mkt): boolean {
    if (!cur || cur.fetchedAt == null) return false;
    const age = Date.now() - cur.fetchedAt;
    if (age > 3 * 3600 * 1000) return false;
    const today = new Date().toDateString();
    return new Date(cur.fetchedAt).toDateString() === today;
  }
  // Подтягиваем тикеры без кеша ИЛИ с устаревшим кешем (см. isFresh). force — игнорировать свежесть.
  async function loadMissing(force = false) {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      for (const t of tickers) {
        const cur = mkt[t.t];
        if (!force && cur && cur.sigmaFull != null && isFresh(cur)) continue;   // свежее → не трогаем
        if (!force && cur && cur.error && isFresh(cur)) continue;               // свежая ошибка — не долбим
        await loadOne(t);
        await new Promise((r) => setTimeout(r, 250));
      }
    } finally { loadingRef.current = false; }
  }
  useEffect(() => { loadMissing(false); /* eslint-disable-next-line */ }, [tickers.length]);

  // --- производные строки ---
  // Покрытие: зелёный, если годовой доход подушки ≥ стоимости позиции «На 9 мес» (exitFull).
  function covered(t: string): boolean | undefined {
    const cost = mkt[t]?.exitFull; if (cost == null || income <= 0) return undefined; return income >= cost;
  }
  // Средний ход до окупаемости премии (для фильтра «скрыть HV<окуп»).
  function beAvg(t: string): number | null {
    const m = mkt[t]; if (m?.beDownPct == null || m?.beUpPct == null) return null;
    return (m.beDownPct + m.beUpPct) / 2;
  }
  const rows = useMemo(() => {
    let a = tickers.filter((tk) => {
      const m = mkt[tk.t] || {};
      if (cls && tk.cls !== cls) return false;
      if (!(m.ivRank == null || m.ivRank <= ivMax)) return false;
      if (hideUncovered && covered(tk.t) === false) return false;     // скрыть явно не покрытые
      if (hideHvLow) {                                                  // скрыть «размах < окуп»
        // Типичный макс. годовой ход (rBest, доля) против среднего хода до точек нуля (ref, %).
        // Если актив исторически не доходит даже в лучшую сторону — отсеиваем.
        const ref = beAvg(tk.t);
        if (m.rBest != null && ref != null && m.rBest * 100 < ref) return false;
      }
      return true;
    });
    const val = (tk: Ticker): number | string | null => {
      const m = mkt[tk.t] || {};
      switch (sortKey) {
        case "iv": return m.ivRank ?? null;
        case "prem": return m.sigmaFull ?? null;
        case "p9": return m.exitFull ?? null;
        case "exp": return m.expiry ?? null;
        case "hv": return m.rBest ?? null;
      }
    };
    const dir = sortDir === "asc" ? 1 : -1;
    a = [...a].sort((x, y) => {
      const av = val(x), bv = val(y);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;   // нет данных — всегда вниз
      if (bv == null) return -1;
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
    return a;
  }, [tickers, mkt, cls, ivMax, sortKey, sortDir, hideUncovered, hideHvLow, income]);
  const shown = expanded ? rows : rows.slice(0, LIMIT);

  // --- FLIP-анимация ТОЛЬКО при ручной смене сортировки (не при подгрузке данных) ---
  // Иначе список «дёргается», когда тикеры догружаются и переезжают по IV Rank.
  const lastSortRef = useRef(`${sortKey}:${sortDir}`);
  useLayoutEffect(() => {
    const body = tbodyRef.current; if (!body) return;
    const sig = `${sortKey}:${sortDir}`;
    const animate = lastSortRef.current !== sig;   // анимируем перестановку только при смене сортировки/направления
    lastSortRef.current = sig;
    const trs = Array.from(body.querySelectorAll("tr[data-tk]")) as HTMLElement[];
    if (animate) {
      trs.forEach((tr) => {
        const tk = tr.dataset.tk!; const now = tr.getBoundingClientRect().top;
        const prev = flipPrev.current[tk];
        if (prev != null && Math.abs(prev - now) > 1) {
          tr.style.transform = `translateY(${prev - now}px)`; tr.style.transition = "none";
          requestAnimationFrame(() => { tr.style.transition = ""; tr.style.transform = ""; });
        }
      });
    }
    const np: Record<string, number> = {};
    trs.forEach((tr) => { np[tr.dataset.tk!] = tr.getBoundingClientRect().top; });
    flipPrev.current = np;
  });

  // --- управление тикерами ---
  async function onAdd() {
    const v = addInp.trim().toUpperCase(); if (!v) return;
    if (tickers.some((t) => t.t === v)) { setAddInp(""); return; }
    setAdding(true); setAddErr(null);
    const sym = PROXY[v] ?? v;
    try {
      await api.analyzeStrangle({ symbol: sym, lot_multiplier: 100, capital: cons.sum, rate: cons.rate });
      const saved = await api.addTrackedSymbol({ symbol: v, data_symbol: sym, name: "Новый инструмент", cls: "—" })
        .catch(() => null);   // старый бэк без endpoint'а → добавляем хотя бы локально
      setTickers((a) => [...a, saved
        ? { t: saved.symbol, n: saved.name || "Новый инструмент", cls: saved.cls || "—", prio: saved.priority, trig: saved.trigger, sdate: saved.signal_date }
        : { t: v, n: "Новый инструмент", cls: "—" }]);
      setAddInp("");
    } catch (e: any) { setAddErr(`Тикер ${v} не прошёл проверку: ${e.message ?? e}. Не добавлен.`); }
    finally { setAdding(false); }
  }
  function doDelete() {
    if (!delTk) return;
    // Серверу — «скрыть»: IV-история и накопление кроном СОХРАНЯЮТСЯ (track_iv не трогаем)
    api.hideTrackedSymbol(delTk).catch(() => { /* старый бэк — скрываем локально */ });
    setTickers((a) => a.filter((t) => t.t !== delTk));
    setMkt((m) => { const c = { ...m }; delete c[delTk]; return c; });
    setDelTk(null);
  }

  const ivColor = (iv: number) => iv > 50 ? "var(--bad)" : iv >= 40 ? "var(--warn)" : "var(--ok)";
  const ivBg = (iv: number) => iv > 50 ? "var(--bad-soft)" : iv >= 40 ? "var(--warn-soft)" : "var(--ok-soft)";
  // Scout-приоритет: red — сильный триггер, yellow — средний, green — наблюдение
  const prioColor: Record<string, string> = { red: "var(--bad)", yellow: "var(--warn)", green: "var(--ok)" };
  const prioName: Record<string, string> = { red: "высокий", yellow: "средний", green: "наблюдение" };

  return (
    <>
      <div className="row-head">
        <div>
          <div className="h1">Вотчлист</div>
          <div className="sub">Приоритетный список: чем ниже волатильность (IV Rank), тем выше актив — дешёвый вход к покупке наверху. Стрэнгл считается на 3% OTM.</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setAddErr(null); setManageOpen(true); }}>＋ Управление тикерами</button>
      </div>

      {/* статус обновления */}
      <div className="updbar">
        <span className="clock"></span>
        <span>Последнее обновление: <b>{ivSnap?.recorded_at ? new Date(ivSnap.recorded_at).toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" }) : "—"}</b></span>
        <span className="sep">·</span>
        <span>Котировки и IV Rank обновляются <b style={{ fontFamily: "inherit" }}>3 раза в день</b> — открытие, середина и закрытие сессии (вручную обновлять не нужно).</span>
        {ivSnap?.last_phase && <><span className="sep">·</span><span>фаза: <b style={{ fontFamily: "inherit" }}>{ivSnap.last_phase}</b></span></>}
      </div>

      {/* конс. часть */}
      <div className="cons">
        <span className="ttl">Консервативная часть (подушка)</span>
        <label>Сумма $ <input value={cons.sum} onChange={(e) => setCons((c) => ({ ...c, sum: Number(e.target.value) || 0 }))} /></label>
        <label>% годовых <input value={cons.rate} onChange={(e) => setCons((c) => ({ ...c, rate: Number(e.target.value) || 0 }))} /></label>
        <span className="arrow">→</span>
        <span className="out">доход <b>{f0(income)}</b> в год</span>
        <span className="hintc">Покрытие в таблице считается от этой суммы: зелёный, если годовой доход подушки ≥ стоимости позиции «На 9 мес». У каждого своя подушка — $500 или $5 000.</span>
      </div>

      {/* фильтры/сортировка */}
      <div className="controls">
        <div className="field"><span className="mute">Сортировка</span>
          <select value={sortKey} onChange={(e) => { setSortKey(e.target.value as SortKey); setSortDir("asc"); }}>
            <option value="iv">IV Rank — приоритет</option>
            <option value="prem">Стрэнгл 3% — стоимость</option>
            <option value="p9">На 9 мес</option>
            <option value="exp">Экспирация</option>
            <option value="hv">Размах за год</option>
          </select>
        </div>
        <div className="field"><span className="mute">Класс</span>
          <select value={cls} onChange={(e) => setCls(e.target.value)}>
            <option value="">Любой</option>{["Металлы", "Сырьё", "Индексы", "FX", "Облигации", "Крипто"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="field"><span className="mute">IV Rank ≤</span>
          <input type="text" value={ivMax} onChange={(e) => setIvMax(Number(e.target.value) || 100)} style={{ width: 40 }} />
        </div>
        <button className={`chip ${hideUncovered ? "on" : ""}`} onClick={() => setHideUncovered((v) => !v)}
          title="Показывать только те, где доход подушки покрывает стоимость «На 9 мес»">
          Скрыть не покрытые
        </button>
        <button className={`chip ${hideHvLow ? "on" : ""}`} onClick={() => setHideHvLow((v) => !v)}
          title="Скрыть тикеры, у которых типичный годовой размах (макс. ход за год) меньше среднего хода до точек нуля — актив исторически не доходит до окупаемости даже в лучшую сторону">
          Скрыть размах &lt; окуп.
        </button>
      </div>

      <div className="card">
        <div className="tablewrap">
          <table className="wl-tight">
            <colgroup>
              <col style={{ width: 30 }} />{/* # */}
              <col style={{ width: 224 }} />{/* Тикер */}
              <col style={{ width: 94 }} />{/* Цена (только spot) */}
              <col style={{ width: 102 }} />{/* Эксп. */}
              <col style={{ width: 128 }} />{/* Стрэнгл 3% (+ C/P под ним) */}
              <col style={{ width: 114 }} />{/* На 9 мес */}
              <col style={{ width: 54 }} />{/* Покр. */}
              <col style={{ width: 104 }} />{/* IV Rank — слева тонкая линия-разделитель */}
              <col style={{ width: 78 }} />{/* HV */}
              <col style={{ width: 92 }} />{/* Окуп. (крайний правый) */}
            </colgroup>
            <thead><tr>
              <th className="l">#</th>
              <th className="l">Тикер</th>
              <th><Tip text={tip(LBL.spot.t!)}>Цена</Tip></th>
              <th className={sortKey === "exp" ? "sorted" : ""} style={{ cursor: "pointer" }} onClick={() => clickSort("exp")}>
                <Tip text={tip(LBL.expiry.t!)}>Эксп.</Tip>{arrow("exp")}
              </th>
              <th className={sortKey === "prem" ? "sorted" : ""} style={{ cursor: "pointer" }} onClick={() => clickSort("prem")}>
                <Tip text={tip(LBL.sigmaPremium.t!)}>Стрэнгл 3%</Tip>{arrow("prem")}
              </th>
              <th className={sortKey === "p9" ? "sorted" : ""} style={{ cursor: "pointer" }} onClick={() => clickSort("p9")}>
                <Tip text={tip(LBL.exitValue.t!)}>На 9 мес</Tip>{arrow("p9")}
              </th>
              <th className="c"><Tip text={tip(LBL.coverage.t!)}>Покр.</Tip></th>
              <th className={`sep-left ${sortKey === "iv" ? "sorted" : ""}`} style={{ cursor: "pointer" }} onClick={() => clickSort("iv")}>
                <Tip text={tip(LBL.ivRank.t!)}>IV Rank</Tip>{arrow("iv")}
              </th>
              <th className={sortKey === "hv" ? "sorted" : ""} style={{ cursor: "pointer" }} onClick={() => clickSort("hv")}>
                <Tip text={"Реальный годовой РАЗМАХ цены, а не усреднённая HV. Считается по скользящим годовым окнам: насколько далеко цена уходила от точки входа (макс. ход в лучшую сторону, медиана по всем окнам). Снизу — фактический ход вверх/вниз за последний год. Именно это важно стрэнглу: дойдёт ли цена до точек нуля. HV сглаживает и занижает — напр. у Apple HV~20%, а реальный годовой ход ±40%."}>Размах/год</Tip>{arrow("hv")}
              </th>
              <th><Tip text={"Насколько % цена должна сойти вниз/вверх от спота, чтобы окупить премию (точки нуля). Сверху — вниз, снизу — вверх."}>Окуп.</Tip></th>
            </tr></thead>
            <tbody ref={tbodyRef}>
              {shown.map((tk, i) => {
                const m = mkt[tk.t] || {};
                const cov = covered(tk.t);
                const decay = (m.sigmaFull != null && m.exitFull != null) ? m.sigmaFull - m.exitFull : undefined;
                const src = m.source === "tradier" ? { i: "🅣", t: "Цепочка: Tradier (реальные Bid/Ask)" } : m.source ? { i: "🆈", t: "Цепочка: yfinance" } : null;
                return (
                  <tr key={tk.t} data-tk={tk.t}>
                    <td className="l"><span className={`rank ${i < 3 ? "top" : ""}`}>{i + 1}</span></td>
                    <td className="l">
                      <div className="tk tk-link" onClick={() => openAnalyzer(tk)} role="button"
                        title={`Открыть ${tk.t} в Аналитике — соберём стрэнгл ${PROXY[tk.t] ? `(через ${PROXY[tk.t]}) ` : ""}и построим график`}>
                        <div className="bd">{tk.t.replace("/", "").slice(0, 2)}</div>
                        <div>
                          <div className="nm">{tk.t} {tk.prio && prioColor[tk.prio] && (
                            <span title={`Scout: приоритет ${prioName[tk.prio] ?? tk.prio}${tk.sdate ? ` (сигнал ${tk.sdate})` : ""}${tk.trig ? ` — ${tk.trig}` : ""}`}
                              style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: prioColor[tk.prio], marginLeft: 2, verticalAlign: "middle", cursor: "help" }} />
                          )} {PROXY[tk.t] && <span style={{ fontSize: 10, color: "var(--txt-3)" }}>→{PROXY[tk.t]}</span>} {m.ivRank == null && !m.loading && m.fetchedAt && <span className="newtag">новый</span>} {src && <span className="srcbadge" title={src.t}>{src.i}</span>}</div>
                          <div className="ds" title={tk.trig || undefined}>{tk.n} · {tk.cls}{m.loading ? " · загрузка…" : ""}{m.error ? " · нет данных" : ""} <span className="tk-go">→ Аналитик</span></div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="cell-main num">{m.spot != null ? m.spot.toFixed(2) : "—"}</div>
                    </td>
                    <td>
                      <div className="num" style={{ color: "var(--txt-2)" }}>{expFmt(m.expiry)}</div>
                      {m.days != null && <div className="csub">{m.days} дн</div>}
                    </td>
                    <td>
                      <div className="cell-main num">{f0(m.sigmaFull)}</div>
                      {m.callK != null && m.putK != null && <div className="csub">C {fk(m.callK)} · P {fk(m.putK)}</div>}
                    </td>
                    <td>
                      {m.exitFull == null ? <span className="csub">—</span> : <><div className="cell-main num">{f0(m.exitFull)}</div>{decay != null && <div className="decay">−{Math.round(decay).toLocaleString("ru")}</div>}</>}
                    </td>
                    <td className="c">
                      {cov === undefined ? <span className="csub">—</span> :
                        <span className="cov" style={{ background: cov ? "var(--ok)" : "var(--bad)" }}
                          title={cov ? "Доход подушки покрывает стоимость «На 9 мес»" : "Доход подушки не покрывает «На 9 мес» — увеличь подушку"}></span>}
                    </td>
                    <td className="sep-left">
                      {m.ivRank == null ? (
                        <span className="ivnote" title={m.ivStatus === "PROXY_VRP" ? "Истории мало — показан прокси (IV − HV30), не настоящий ранг" : "История IV Rank ещё копится (нужно ~30 наблюдений)"}>
                          {m.ivStatus === "PROXY_VRP" ? "прокси" : "копится"}
                        </span>
                      ) : (
                        <span className="ivcell">
                          <span className="ivpill" style={{ color: ivColor(m.ivRank), background: ivBg(m.ivRank) }}>{Math.round(m.ivRank)}%</span>
                          {m.ivStatus === "PROXY_VRP" ? <span className="ivnote" title="Истории мало — показан прокси (IV − HV30)">прокси</span> :
                            m.ivStatus?.startsWith("EARLY") ? <span className="ivnote" title="Ранний ранг: история ещё неполная (7–29 наблюдений)">ранний ранг</span> : null}
                        </span>
                      )}
                    </td>
                    <td title={m.rBest != null
                      ? `Типичный макс. ход за год от входа: ${Math.round(m.rBest * 100)}% (медиана по годовым окнам). За последний год: вверх ${m.rUpLast != null ? Math.round(m.rUpLast * 100) : "—"}%, вниз ${m.rDnLast != null ? Math.round(m.rDnLast * 100) : "—"}%. Для сравнения HV 2г: ${m.hv2y != null ? Math.round(m.hv2y * 100) + "%" : "н/д"}.`
                      : "Реальный годовой размах цены"}>
                      {m.rBest == null ? <span className="csub">—</span> : (
                        <><div className="cell-main num">±{Math.round(m.rBest * 100)}%</div>
                          <div className="csub">{m.rUpLast != null && m.rDnLast != null
                            ? <>год <span style={{ color: "var(--ok)" }}>↑{Math.round(m.rUpLast * 100)}</span> <span style={{ color: "var(--bad)" }}>↓{Math.round(m.rDnLast * 100)}</span></>
                            : "—"}</div></>
                      )}
                    </td>
                    <td title={m.beLow != null && m.beHigh != null ? `Точки нуля: ${m.beLow.toFixed(2)} / ${m.beHigh.toFixed(2)} (вниз/вверх от спота ${m.spot?.toFixed(2)})` : "Насколько цена должна сойти вниз/вверх от спота, чтобы окупить премию"}>
                      {m.beDownPct != null && m.beUpPct != null ? (
                        <><div className="cell-main num" style={{ color: "var(--bad)" }}>↓{m.beDownPct.toFixed(1)}%</div>
                          <div className="csub" style={{ color: "var(--ok)" }}>↑{m.beUpPct.toFixed(1)}%</div></>
                      ) : <span className="csub">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > LIMIT && (
          <button className="showmore" onClick={() => setExpanded((x) => !x)}>
            {expanded ? "Свернуть" : `Показать больше (+${rows.length - LIMIT})`}
          </button>
        )}
      </div>

      <div className="legend">
        <span><span className="cdot" style={{ background: "var(--ok)" }}></span>IV Rank &lt; 40% — дешёвый вход</span>
        <span><span className="cdot" style={{ background: "var(--warn)" }}></span>40–50% — средне</span>
        <span><span className="cdot" style={{ background: "var(--bad)" }}></span>&gt; 50% — дорого</span>
        <span><span className="cdot" style={{ background: "var(--ok)" }}></span>Покр. — депозит покрывает премию</span>
        <span>Точка у тикера — приоритет Scout (наведи: триггер сигнала)</span>
        <span>🅣 Tradier · 🆈 yfinance</span>
      </div>

      {/* модал управления */}
      <div className={`aikido-overlay ${manageOpen ? "show" : ""}`} onClick={(e) => { if ((e.target as HTMLElement).classList.contains("aikido-overlay")) setManageOpen(false); }}>
        <div className="modal">
          <div className="modal-h">Управление тикерами <button className="icon-btn" onClick={() => setManageOpen(false)}>✕</button></div>
          <div className="modal-b">
            <div className="addrow">
              <input placeholder="Например: TLT" maxLength={8} value={addInp} onChange={(e) => setAddInp(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }} disabled={adding} />
              <button className="btn btn-primary" onClick={onAdd} disabled={adding || !addInp.trim()}>{adding ? "Проверяю…" : "Добавить"}</button>
            </div>
            {addErr && <div style={{ fontSize: 12, color: "var(--bad)", marginBottom: 10 }}>{addErr}</div>}
            <div style={{ fontSize: 12, color: "var(--txt-3)", marginBottom: 10 }}>Добавленный тикер начнёт обновляться 3 раза в день. История IV Rank копится несколько недель — до её накопления ранг помечается «накопл.».</div>
            <div className="tklist">
              {tickers.map((t) => (
                <div className="tkitem" key={t.t}>
                  <div><div className="nm">{t.t}</div><div className="meta">{t.n} · {t.cls}</div></div>
                  <button className="x" onClick={() => setDelTk(t.t)}>×</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* подтверждение удаления */}
      <div className={`aikido-overlay ${delTk ? "show" : ""}`} onClick={(e) => { if ((e.target as HTMLElement).classList.contains("aikido-overlay")) setDelTk(null); }}>
        <div className="modal" style={{ maxWidth: 420 }}>
          <div className="modal-h">Удалить тикер?</div>
          <div className="modal-b">
            <div className="alert">Тикер <b>{delTk}</b> будет скрыт из вотчлиста. <b>История IV Rank сохраняется</b> — накопление продолжится на сервере, и при повторном добавлении ранг вернётся сразу, без потери недель истории.</div>
            <div className="modal-f">
              <button className="btn btn-ghost" onClick={() => setDelTk(null)}>Отмена</button>
              <button className="btn-danger" onClick={doDelete}>Скрыть из списка</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
